import * as React from "react";
import {
  SortOrder,
  type InternalSorterState,
  type TableColumn,
  type TableProps,
  type TableSorter,
} from "./types";

export function resolveRowKey<T>(
  rowKey: TableProps<T>["rowKey"],
): (row: T, index: number) => React.Key {
  if (typeof rowKey === "function") return (row, index) => rowKey(row, index);
  if (typeof rowKey === "string") return (row) => (row as any)?.[rowKey];
  return (row, index) => (row as any)?.id ?? index;
}

export function getTableStyle(
  scroll: TableProps<any>["scroll"],
): React.CSSProperties | undefined {
  if (!scroll?.x) return undefined;
  return {
    minWidth:
      scroll.x === true
        ? "max-content"
        : typeof scroll.x === "number"
          ? `${scroll.x}px`
          : scroll.x,
  };
}

export function columnHasSorter<T>(column: TableColumn<T>) {
  return !!column.sorter;
}

export function getColumnSortOrder<T>(
  column: TableColumn<T>,
  sorterState: InternalSorterState,
) {
  if (sorterState && sorterState.columnKey === column.key)
    return sorterState.order;
  return null;
}

export function cycleSortOrder(order: SortOrder | null) {
  if (!order) return SortOrder.ascend;
  if (order === SortOrder.ascend) return SortOrder.descend;
  return null;
}

export function buildSorterPayload(
  sorterState: InternalSorterState,
): TableSorter | undefined {
  if (!sorterState) return undefined;
  return {
    field: sorterState.columnKey,
    order: sorterState.order,
  };
}

export function toInternalSorter(sorter?: TableSorter): InternalSorterState {
  if (!sorter) return null;
  return { columnKey: sorter.field, order: sorter.order };
}

export function nextSorterState<T>(
  column: TableColumn<T>,
  sorterState: InternalSorterState,
): InternalSorterState {
  if (!columnHasSorter(column) || column.key == null) return sorterState;

  const nextOrder = cycleSortOrder(getColumnSortOrder(column, sorterState));
  if (!nextOrder) return null;
  return { columnKey: column.key, order: nextOrder };
}
